import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { DistributionAreasSection } from "./DistributionAreasSection";

interface ExpandMapModalProps {
  open: boolean;
  onClose: () => void;
  /** City name for the header (the City control is intentionally absent — AC26). */
  cityName?: string;
  /** The channel map, mounted in its modal variant (search, no nested expand). */
  children: React.ReactNode;
}

// The full-screen Expand-map modal (AC25/AC26): the selected-areas list on the
// left and the map on the right. Selection state lives in TargetingContext, so
// the modal map and the inline map stay in sync automatically (T13) — the modal
// mounts its own map instance rather than sharing one. The City cannot be
// changed here (no City control rendered); closing (X / Esc / overlay) leaves
// all selections intact because nothing is reset on close.
export const ExpandMapModal = ({ open, onClose, cityName, children }: ExpandMapModalProps) => (
  // modal={false}: a modal Dialog locks body pointer-events + traps focus, which
  // breaks the H2H date/time pickers and the remove-confirm — they portal to
  // <body>, outside this content, so the lock makes them un-hoverable and clicks
  // fall through behind them. Non-modal keeps them interactive; Esc / overlay /
  // X still close via onOpenChange. The overlay below still dims the page.
  <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()} modal={false}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-[1300] bg-[rgba(0,0,0,0.5)]" />
      <DialogPrimitive.Content
        // cb-v2-root: the builder scopes box-sizing/border/font under this class
        // (Tailwind preflight is disabled app-wide). Radix portals to document.body,
        // outside the shell, so without it the map's toast/Add dialog render
        // content-box and inflate (padding added onto fixed heights).
        className="cb-v2-root fixed left-1/2 top-1/2 z-[1300] flex h-[90vh] w-[95vw] max-w-[1440px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-solid border-border bg-background shadow-xl focus:outline-none"
        data-cypress-id="targeting-expand-modal"
      >
        <div className="flex items-center justify-between border-0 border-b border-solid border-border px-6 py-4">
          <DialogPrimitive.Title className="text-base font-semibold text-foreground">
            {cityName ? `Distribution areas in ${cityName}` : "Distribution areas"}
          </DialogPrimitive.Title>
          <DialogPrimitive.Close
            aria-label="Close"
            data-cypress-id="targeting-expand-modal-close"
            className="rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="size-5" />
          </DialogPrimitive.Close>
        </div>
        <div className="flex min-h-0 flex-1">
          <div className="w-[460px] shrink-0 overflow-y-auto border-0 border-r border-solid border-border p-5">
            <DistributionAreasSection />
          </div>
          <div className="relative min-w-0 flex-1">{children}</div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);
